import { IonContent, IonTextarea, IonHeader, IonButtons, IonBackButton, IonPage, IonTitle, IonToolbar, IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonItem, IonButton, IonInput, IonLabel, IonSelect, IonSelectOption, useIonRouter, useIonAlert, IonIcon, IonSpinner } from '@ionic/react';
import EnvironmentBadge from '../components/EnvironmentBadge';
import './TimeRegistration.css';
import getApi from '../api';
import React, { useState, useEffect } from 'react';
import CustomerSearchModal from '../components/CustomerSearchModal';
import { search, checkmarkCircleOutline, closeCircleOutline } from 'ionicons/icons';

const TimeRegistration: React.FC = () => {
  const [date, setDate] = useState<string>('');
  const [checkIn, setCheckIn] = useState<string>('');
  const [checkOut, setCheckOut] = useState<string>('');
  const [hours, setHours] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [orderId, setOrderId] = useState<string>('');
  const [traktamente, setTraktamente] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const router = useIonRouter();
  const [presentAlert] = useIonAlert();

  // State for order validation
  const [isOrderValid, setIsOrderValid] = useState<boolean | null>(null);
  const [isCheckingOrder, setIsCheckingOrder] = useState(false);
  const [orderError, setOrderError] = useState('');

  const api = getApi();

  const checkOrderExists = async (id: string) => {
    if (!id) return;

    setIsCheckingOrder(true);
    setIsOrderValid(null);
    setOrderError('');

    try {
      const api = getApi();
      const response = await api.get(`/fortnox/orders/${id}/`);

      if (response.data.ErrorInformation && response.data.ErrorInformation.Message) {
        setIsOrderValid(false);
        setOrderError(response.data.ErrorInformation.Message);
      } else if (response.data.Order.Sent === true) {
        setIsOrderValid(false);
        setOrderError('Ordern är stängd och kan inte tidregistreras.');
      } else {
        setIsOrderValid(true);
      }

    } catch (error: any) {
      if (error.response && error.response.data && error.response.data.ErrorInformation && error.response.data.ErrorInformation.Message) {
        setIsOrderValid(false);
        setOrderError(error.response.data.ErrorInformation.Message);
      } else if (error.response && error.response.status === 404) {
        setIsOrderValid(false);
        setOrderError('Ordernumret kunde inte hittas.');
      } else {
        setIsOrderValid(false);
        setOrderError('Ett fel uppstod vid validering.');
        console.error("Order validation error:", error);
      }
    } finally {
      setIsCheckingOrder(false);
    }
  };

  // Debounced effect for order validation
  useEffect(() => {
    if (!orderId) {
      setIsOrderValid(null);
      setOrderError('');
      return;
    }

    // Clear previous validation state
    setIsOrderValid(null);
    const handler = setTimeout(() => {
      checkOrderExists(orderId);
    }, 500); // 500ms debounce delay

    return () => {
      clearTimeout(handler);
    };
  }, [orderId]);

  // Automatically calculate hours when checkIn or checkOut changes
  useEffect(() => {
    if (checkIn && checkOut) {
      const startTimestamp = new Date(`1970-01-01T${checkIn}Z`).getTime();
      const stopTimestamp = new Date(`1970-01-01T${checkOut}Z`).getTime();

      if (stopTimestamp <= startTimestamp) {
        setHours('');
        return;
      }

      let diff = stopTimestamp - startTimestamp;
      let h = Math.floor(diff / (1000 * 60 * 60));
      let m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (m >= 45) {
        h += 1;
        m = 0;
      } else if (m >= 15) {
        m = 5;
      } else {
        m = 0;
      }
      setHours(h + '.' + m);
    } else {
      setHours('');
    }
  }, [checkIn, checkOut]);

  const cal_time = () => {
    if (checkIn && checkOut) {
      const startTimestamp = new Date(`1970-01-01T${checkIn}Z`).getTime();
      const stopTimestamp = new Date(`1970-01-01T${checkOut}Z`).getTime();

      if (stopTimestamp <= startTimestamp) {
        presentAlert({
          header: 'Valideringsfel',
          message: 'Sluttiden måste vara efter starttiden.',
          buttons: ['OK'],
        });
        setHours('');
        return;
      }

      let diff = stopTimestamp - startTimestamp;
      let h = Math.floor(diff / (1000 * 60 * 60));
      let m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (m >= 45) {
        h += 1;
        m = 0;
      } else if (m >= 15) {
        m = 5;
      } else {
        m = 0;
      }
      setHours(h + '.' + m);
    }
  };

  async function save() {
    if (!date || !checkIn || !checkOut || !description) {
        presentAlert({
            header: 'Fel',
            message: 'Vänligen fyll i alla obligatoriska fält.',
            buttons: ['OK'],
        });
        return;
    }

    if (new Date(`1970-01-01T${checkOut}Z`) <= new Date(`1970-01-01T${checkIn}Z`)) {
        presentAlert({
            header: 'Valideringsfel',
            message: 'Sluttiden måste vara efter starttiden.',
            buttons: ['OK'],
        });
        return;
    }

    // Validate order number if provided
    if (orderId && isOrderValid !== true) {
        presentAlert({
            header: 'Valideringsfel',
            message: orderError || 'Ange ett giltigt ordernummer.',
            buttons: ['OK'],
        });
        return;
    }

    const timeData = {
        "date": date,
        "check_in": checkIn,
        "check_out": checkOut,
        "desc": description,
        "hours": hours,
        "order_id": orderId,
        "traktamente": traktamente,
        "user": window.localStorage.getItem("me") ? JSON.parse(window.localStorage.getItem("me")!).id : null,
        "edited": "True"
    };

    try {
        const response = await api.post('/user/timeregister/', timeData);
        if (response.status === 201) {
            router.goBack();
        }
    } catch (error) {
        console.error('Error:', error);
        presentAlert({
            header: 'Error',
            message: 'Det gick inte att spara tiden.',
            buttons: ['OK'],
        });
    }
  }

  return (
    <IonPage placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
      <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonButtons slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonBackButton />
          </IonButtons>
          <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Registrera Tid</IonTitle>
          <div slot="end" style={{paddingRight:'8px'}}><EnvironmentBadge /></div>
        </IonToolbar>
      </IonHeader>
    <IonContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
    <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
      <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}></IonCardTitle>
      </IonCardHeader>

      <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Datum</IonLabel>
          <IonInput type="date" value={date} onIonChange={e => setDate(e.detail.value!)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}></IonInput>
        </IonItem>
        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Start</IonLabel>
          <IonInput type="time" value={checkIn} onIonChange={e => setCheckIn(e.detail.value!)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}></IonInput>
        </IonItem>
        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Stop</IonLabel>
          <IonInput type="time" value={checkOut} onIonChange={e => setCheckOut(e.detail.value!)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}></IonInput>
        </IonItem>
        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Arbetad Tid</IonLabel>
          <IonInput type="number" value={hours} disabled={true} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}></IonInput>
        </IonItem>
        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonTextarea
                placeholder="Beskrivning av arbetet"
                value={description}
                onIonChange={e => setDescription(e.detail.value!)} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}            ></IonTextarea>
        </IonItem>
        <IonItem lines="none" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Ordernummer</IonLabel>
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <IonInput
                  type="number"
                  value={orderId}
                  onIonChange={e => setOrderId(e.detail.value!)}
                  placeholder="Ange ordernummer"
                  className="order-input" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}></IonInput>
            <IonButton fill="clear" onClick={() => setIsModalOpen(true)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonIcon icon={search} slot="icon-only" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
            </IonButton>
            {isCheckingOrder && <IonSpinner name="crescent" className="validation-spinner" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} placeholder={undefined}></IonSpinner>}
            {isOrderValid === true && <IonIcon icon={checkmarkCircleOutline} color="success" className="validation-icon" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} placeholder={undefined} />}
            {isOrderValid === false && <IonIcon icon={closeCircleOutline} color="danger" className="validation-icon" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} placeholder={undefined} />}
          </div>
        </IonItem>
        {orderError && (
          <IonItem lines="none" className="error-message-item" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} placeholder={undefined}>
            <IonLabel color="danger" className="ion-text-wrap" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} placeholder={undefined}>
              {orderError}
            </IonLabel>
          </IonItem>
        )}
        <CustomerSearchModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSelectOrder={(selectedOrderId) => {
              setOrderId(selectedOrderId);
              setIsModalOpen(false);
            }}
          />
          <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Traktamente</IonLabel>
            <IonSelect value={traktamente} onIonChange={e => setTraktamente(e.detail.value!)} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} placeholder={undefined}>
          <IonSelectOption value="" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Inget</IonSelectOption>
          <IonSelectOption value="Halv" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Halv</IonSelectOption>
          <IonSelectOption value="Hel" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Hel</IonSelectOption>
        </IonSelect>
      </IonItem>
      <IonButton onClick={save} expand="block" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Spara</IonButton>
      </IonCardContent>
    </IonCard>
    </IonContent>
    </IonPage>
  );
};

export default TimeRegistration;